import { useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useBooking } from '@/features/appointments/hooks/useBooking';
import { AppointmentDetailsContent } from '@/features/appointments/components/AppointmentDetails/AppointmentDetailsContent';
import { usePageLoading } from '@/hooks/usePageLoading';
import { useStanRouter } from '@/hooks/useStanRouter';
import { useBreadcrumbs } from '@/hooks/useBreadcrumbs';
import { stanNotify } from '@/lib/stanNotify';
import { exportBookingToCSV } from '@/lib/utils/csvExport';
import { useCancelAppointment, useRescheduleAppointment } from '@/features/appointments/queries/useAppointmentActions';
import { useUser } from '@/queries/useUser';
import { isAxiosError } from 'axios';

export default function AppointmentDetailsPage() {
    const { bookingId: id } = useParams<{ bookingId: string }>();
    const { push } = useStanRouter();
    const { user } = useUser();

    const { booking, isLoading, isError, error, refetch } = useBooking({
        bookingId: id || '',
    });

    const { mutateAsync: rescheduleAsync, isPending: isRescheduling } = useRescheduleAppointment();
    const { mutateAsync: cancelAsync, isPending: isCanceling } = useCancelAppointment();

    usePageLoading(isLoading);

    const getReturnUrl = () => {
        const returnUrl = sessionStorage.getItem('appointmentsReturnUrl');
        if (returnUrl && returnUrl.startsWith('/appointments')) {
            return returnUrl;
        }
        return '/appointments/calendar';
    };

    const referrerUrl = getReturnUrl();

    useBreadcrumbs([
        { title: 'My Appointments', to: referrerUrl, active: false, id: 'appointments' },
        {
            title: 'Overview',
            to: `/appointments/details/${id}`,
            active: true,
            id: 'appointment-details',
        },
    ]);

    useEffect(() => {
        if (isError && error) {
            stanNotify({
                type: 'error',
                title: 'Failed to load appointment',
                text: error.message || 'The requested appointment could not be found.',
            });
            push('/appointments');
        }
    }, [isError, error, push]);

    const handleExportDetails = (_bookingId: string) => {
        if (!booking) return;
        exportBookingToCSV(booking);
    };

    const handleReschedule = async (data: { date: string; startTime: string; endTime: string; reason?: string }) => {
        if (!booking) return;

        try {
            await rescheduleAsync({
                bookingId: booking.booking_id,
                ...data,
            });
            // Refetch booking data after successful reschedule
            await refetch();
        } catch (error: unknown) {
            const errorMessage = isAxiosError(error)
                ? (error.response?.data?.message ?? 'Failed to reschedule appointment. Please try again.')
                : 'Failed to reschedule appointment. Please try again.';
            stanNotify({
                type: 'error',
                title: 'Reschedule Failed',
                text: errorMessage,
            });
            throw error; // Re-throw so modal knows it failed
        }
    };

    const handleCancel = async () => {
        if (!booking) return;

        try {
            await cancelAsync({
                bookingId: booking.booking_id,
                userId: user?.user_id,
            });
            stanNotify({
                type: 'success',
                title: 'Appointment Canceled',
                text: 'The appointment has been successfully canceled.',
            });
            // Navigate back to appointments list after canceling
            push(referrerUrl);
        } catch (err: unknown) {
            const error = err as { response?: { data?: { message?: string } } };
            const errorMessage = error?.response?.data?.message || 'Failed to cancel appointment. Please try again.';
            stanNotify({
                type: 'error',
                title: 'Cancel Failed',
                text: errorMessage,
            });
        }
    };

    if (!booking) {
        return <div />;
    }

    return (
        <AppointmentDetailsContent
            booking={booking}
            onExportDetails={handleExportDetails}
            onReschedule={handleReschedule}
            onCancel={handleCancel}
            isRescheduling={isRescheduling}
            isCanceling={isCanceling}
        />
    );
}
